import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { hexCss, resolveTimeOfDayBand, timeOfDayVisual, type TimeOfDayBand } from '../sim/timeOfDay';
import { expansionCameraTier } from '../spatial/plots';
import { applyNightLook } from '../spikes/threejs/glow';
import { createChannelMaterials, createMaterials } from '../spikes/threejs/materials';
import { CAROUSEL_PALETTES, COASTER_PALETTES } from '../spikes/threejs/palettes';
import { applyApprovedPalettesToChannels, type MigrationRenderModel } from './mapParkState';
import {
  SHARED_LIGHT_CHANNELS,
  createParkFromState,
  syncParkEntityLayer,
  syncParkFloorLayers,
  syncParkLandOverlayLayer,
  syncParkSelectionLayer,
} from './parkFromState';
import type { QueueEvidence } from './queuePresentation';

export type CameraFrameMode = 'portrait' | 'narrow' | 'desktop';

export interface ProductionCameraFraming {
  mode: CameraFrameMode;
  viewHeight: number;
  shiftX: number;
  shiftY: number;
}

export const CAMERA_AZIMUTH = THREE.MathUtils.degToRad(41);
export const CAMERA_ELEVATION = THREE.MathUtils.degToRad(37);
export const CAMERA_DISTANCE = 42;
export const CAMERA_LOOK_TARGET = new THREE.Vector3(0, 0.6, 0.4);
export const CAMERA_QUARTER_TURN = Math.PI / 2;
export const PRODUCTION_ZOOM_DEFAULT = 1;
export const PRODUCTION_ZOOM_MIN = 0.7;
export const PRODUCTION_ZOOM_MAX = 1.75;
export const PRODUCTION_ZOOM_STEP = 0.12;
export const WHEEL_ZOOM_PIXEL_SCALE = 0.00125;

export interface ProductionPanOffset {
  x: number;
  z: number;
}

export interface ProductionPanBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export const PRODUCTION_PAN_DEFAULT: ProductionPanOffset = { x: 0, z: 0 };
/** Fallback half-extent when no land specs are available. */
export const PRODUCTION_PAN_FALLBACK_EXTENT = 8;

export function cameraLookTargetWithPan(pan: ProductionPanOffset = PRODUCTION_PAN_DEFAULT): THREE.Vector3 {
  return new THREE.Vector3(
    CAMERA_LOOK_TARGET.x + pan.x,
    CAMERA_LOOK_TARGET.y,
    CAMERA_LOOK_TARGET.z + pan.z,
  );
}

export function productionPanBoundsFromLands(
  lands: ReadonlyArray<{ x: number; z: number; width: number; depth: number; unlocked?: boolean }>,
): ProductionPanBounds {
  const unlocked = lands.filter((land) => land.unlocked === true);
  const source = unlocked.length > 0 ? unlocked : lands;
  if (source.length === 0) {
    return {
      minX: -PRODUCTION_PAN_FALLBACK_EXTENT,
      maxX: PRODUCTION_PAN_FALLBACK_EXTENT,
      minZ: -PRODUCTION_PAN_FALLBACK_EXTENT,
      maxZ: PRODUCTION_PAN_FALLBACK_EXTENT,
    };
  }
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const land of source) {
    const halfX = Math.max(land.width, 0) / 2;
    const halfZ = Math.max(land.depth, 0) / 2;
    minX = Math.min(minX, land.x - halfX);
    maxX = Math.max(maxX, land.x + halfX);
    minZ = Math.min(minZ, land.z - halfZ);
    maxZ = Math.max(maxZ, land.z + halfZ);
  }
  return {
    minX: minX - CAMERA_LOOK_TARGET.x,
    maxX: maxX - CAMERA_LOOK_TARGET.x,
    minZ: minZ - CAMERA_LOOK_TARGET.z,
    maxZ: maxZ - CAMERA_LOOK_TARGET.z,
  };
}

export function clampProductionPan(
  x: number,
  z: number,
  bounds: ProductionPanBounds,
): ProductionPanOffset {
  const safeX = Number.isFinite(x) ? x : 0;
  const safeZ = Number.isFinite(z) ? z : 0;
  const minX = Math.min(bounds.minX, bounds.maxX);
  const maxX = Math.max(bounds.minX, bounds.maxX);
  const minZ = Math.min(bounds.minZ, bounds.maxZ);
  const maxZ = Math.max(bounds.minZ, bounds.maxZ);
  return {
    x: Math.min(maxX, Math.max(minX, safeX)),
    z: Math.min(maxZ, Math.max(minZ, safeZ)),
  };
}

export function normalizedCameraQuarter(value: number): number {
  return ((Math.trunc(value) % 4) + 4) % 4;
}

export function cameraAzimuthForQuarter(quarter: number): number {
  return CAMERA_AZIMUTH + normalizedCameraQuarter(quarter) * CAMERA_QUARTER_TURN;
}

/** Q/E rotate the park view. Modifier chords stay unused so browser shortcuts still work. */
export function parkViewRotateDeltaFromKey(
  key: string,
  modifiers?: { meta?: boolean; ctrl?: boolean; alt?: boolean },
): -1 | 1 | null {
  if (modifiers?.meta || modifiers?.ctrl || modifiers?.alt) {
    return null;
  }
  const normalized = key.length === 1 ? key.toLowerCase() : key;
  if (normalized === 'q') {
    return -1;
  }
  if (normalized === 'e') {
    return 1;
  }
  return null;
}

export function clampProductionZoom(value: number): number {
  if (!Number.isFinite(value)) {
    return PRODUCTION_ZOOM_DEFAULT;
  }
  return Math.min(PRODUCTION_ZOOM_MAX, Math.max(PRODUCTION_ZOOM_MIN, value));
}

/** Zoom in shrinks the orthographic view; zoom out enlarges it. */
export function effectiveOrthographicViewHeight(baseViewHeight: number, zoomFactor: number): number {
  return baseViewHeight / clampProductionZoom(zoomFactor);
}

export function productionZoomFraming(
  framing: ProductionCameraFraming,
  zoomFactor: number,
): ProductionCameraFraming {
  return {
    ...framing,
    viewHeight: effectiveOrthographicViewHeight(framing.viewHeight, zoomFactor),
  };
}

/** Wheel/trackpad: positive deltaY zooms out. Line/page modes convert to pixels first. */
export function zoomDeltaFromWheel(deltaY: number, deltaMode = 0): number {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 800 : deltaY;
  return -pixels * WHEEL_ZOOM_PIXEL_SCALE;
}

/** Keyboard +/- zoom. Modifier chords stay unused so browser zoom still works. */
export function parkViewZoomDeltaFromKey(
  key: string,
  modifiers?: { meta?: boolean; ctrl?: boolean; alt?: boolean },
): number | null {
  if (modifiers?.meta || modifiers?.ctrl || modifiers?.alt) {
    return null;
  }
  if (key === '+' || key === '=') {
    return PRODUCTION_ZOOM_STEP;
  }
  if (key === '-' || key === '_') {
    return -PRODUCTION_ZOOM_STEP;
  }
  return null;
}

/** HUD-safe NDC band so slots stay tappable above the bottom panel and below the top bar. */
export function playableNdcBounds(mode: CameraFrameMode): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} {
  if (mode === 'portrait') {
    return { minX: -0.86, maxX: 0.86, minY: -0.36, maxY: 0.7 };
  }
  if (mode === 'narrow') {
    return { minX: -0.9, maxX: 0.9, minY: -0.52, maxY: 0.78 };
  }
  return { minX: -0.92, maxX: 0.92, minY: -0.7, maxY: 0.82 };
}

export function resolveProductionCameraFraming(
  width: number,
  height: number,
  unlockedLandCount: number,
): ProductionCameraFraming {
  const aspect = width / Math.max(height, 1);
  const extra = unlockedLandCount >= 3 ? 1.6 : 0;
  if (aspect < 0.85) {
    const portraitExtra = unlockedLandCount >= 3 ? 6.2 : unlockedLandCount >= 2 ? 2.2 : 0;
    return {
      mode: 'portrait',
      viewHeight: 33.4 + portraitExtra,
      shiftX: 0.08,
      // UI-019-C1-R1: -2.35 held the look target near 43% from the top and ended the
      // island near 65%, leaving the lower third empty. 3.1 drops a 3-land island to
      // about 38–80% of the portrait height. viewHeight is unchanged so expanded slots
      // stay inside playableNdcBounds; pan/zoom still reach anything at the edge.
      shiftY: 3.1,
    };
  }
  if (aspect < 1.3) {
    return {
      mode: 'narrow',
      viewHeight: 20.8 + extra,
      shiftX: 0,
      shiftY: 0.1,
    };
  }
  return {
    mode: 'desktop',
    viewHeight: (aspect < 1.6 ? 18.4 : 17.2) + extra,
    shiftX: 0,
    shiftY: 0.45,
  };
}

export function applyProductionCameraFraming(
  camera: THREE.OrthographicCamera,
  framing: ProductionCameraFraming,
  width: number,
  height: number,
  azimuth = CAMERA_AZIMUTH,
  pan: ProductionPanOffset = PRODUCTION_PAN_DEFAULT,
): void {
  const aspect = width / Math.max(height, 1);
  const cameraOffset = new THREE.Vector3(
    Math.sin(azimuth) * Math.cos(CAMERA_ELEVATION),
    Math.sin(CAMERA_ELEVATION),
    Math.cos(azimuth) * Math.cos(CAMERA_ELEVATION),
  ).multiplyScalar(CAMERA_DISTANCE);
  const lookTarget = cameraLookTargetWithPan(pan);
  camera.left = (-framing.viewHeight * aspect) / 2 + framing.shiftX;
  camera.right = (framing.viewHeight * aspect) / 2 + framing.shiftX;
  camera.top = framing.viewHeight / 2 + framing.shiftY;
  camera.bottom = -framing.viewHeight / 2 + framing.shiftY;
  camera.position.copy(lookTarget).add(cameraOffset);
  camera.lookAt(lookTarget);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
}

export interface SceneHostLifecycle {
  hostCreates: number;
  parkRebuilds: number;
  floorSyncs: number;
  selectionSyncs: number;
  landSyncs: number;
  entitySyncs: number;
}

export interface MigrationSceneHost {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  canvas: HTMLCanvasElement;
  parkTop: number;
  model: MigrationRenderModel;
  lifecycle: SceneHostLifecycle;
  rebuild(model: MigrationRenderModel): void;
  syncFloors(model: MigrationRenderModel): void;
  syncSelection(model: MigrationRenderModel): void;
  syncLandOverlays(model: MigrationRenderModel): void;
  syncEntities(model: MigrationRenderModel): void;
  applyApprovedPalettes(model: MigrationRenderModel): { carouselPaletteId: string; coasterPaletteId: string };
  renderOnce(): void;
  startLoop(onFrame?: (visualDeltaMs: number, incomeDeltaMs?: number) => void): void;
  resize(): void;
  rotateView(delta: -1 | 1): number;
  viewQuarter(): number;
  zoomBy(delta: number): number;
  setZoomFactor(value: number): number;
  zoomFactor(): number;
  panBy(delta: ProductionPanOffset): ProductionPanOffset;
  setPanOffset(value: ProductionPanOffset): ProductionPanOffset;
  panOffset(): ProductionPanOffset;
  elapsedSeconds(): number;
  queueEvidence(): QueueEvidence;
  advanceElapsed(seconds: number): QueueEvidence;
}

/** Visual/render/visitor motion cap. Foreground income does not use this cap. */
export const VISUAL_FRAME_CAP_SECONDS = 0.05;

export interface ForegroundLoopClock {
  lastVisualMs: number;
  incomeAnchorMs: number;
  suppressIncome: boolean;
}

export function createForegroundLoopClock(nowMs: number, documentHidden: boolean): ForegroundLoopClock {
  return {
    lastVisualMs: nowMs,
    incomeAnchorMs: nowMs,
    suppressIncome: documentHidden,
  };
}

/** Drop hidden time from the income anchor. Visual elapsed stays on its own clock. */
export function noteForegroundVisibility(
  clock: ForegroundLoopClock,
  nowMs: number,
  documentHidden: boolean,
): ForegroundLoopClock {
  if (documentHidden) {
    return { ...clock, suppressIncome: true };
  }
  return {
    ...clock,
    incomeAnchorMs: nowMs,
    suppressIncome: false,
  };
}

/**
 * Visual delta stays capped at 0.05s.
 * Income receives the raw gap only while the document is visible and the gap is not a hidden interval.
 */
export function stepForegroundLoopClock(
  clock: ForegroundLoopClock,
  nowMs: number,
  documentHidden: boolean,
): { clock: ForegroundLoopClock; visualDeltaMs: number; incomeDeltaMs: number } {
  const rawSeconds = Math.max(0, (nowMs - clock.lastVisualMs) / 1000);
  const visualDeltaMs = Math.min(rawSeconds, VISUAL_FRAME_CAP_SECONDS) * 1000;
  if (documentHidden || clock.suppressIncome) {
    return {
      clock: {
        lastVisualMs: nowMs,
        incomeAnchorMs: nowMs,
        suppressIncome: documentHidden,
      },
      visualDeltaMs,
      incomeDeltaMs: 0,
    };
  }
  return {
    clock: {
      lastVisualMs: nowMs,
      incomeAnchorMs: nowMs,
      suppressIncome: false,
    },
    visualDeltaMs,
    incomeDeltaMs: Math.max(0, nowMs - clock.incomeAnchorMs),
  };
}

function documentIsHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

export function createMigrationSceneHost(
  mount: HTMLElement,
  model: MigrationRenderModel,
): MigrationSceneHost {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.32;
  pmrem.dispose();

  const camera = new THREE.OrthographicCamera(-8, 8, 8, -8, 0.1, 120);
  let currentModel = model;
  let cameraQuarter = 0;
  let userZoom = PRODUCTION_ZOOM_DEFAULT;
  let userPan: ProductionPanOffset = { ...PRODUCTION_PAN_DEFAULT };

  function currentPanBounds(): ProductionPanBounds {
    return productionPanBoundsFromLands(currentModel.lands);
  }

  function frameCamera(width: number, height: number): void {
    userPan = clampProductionPan(userPan.x, userPan.z, currentPanBounds());
    const unlocked = expansionCameraTier(currentModel.parity.unlockedLandIds);
    applyProductionCameraFraming(
      camera,
      productionZoomFraming(resolveProductionCameraFraming(width, height, unlocked), userZoom),
      width,
      height,
      cameraAzimuthForQuarter(cameraQuarter),
      userPan,
    );
  }

  function applyUserPan(next: ProductionPanOffset): ProductionPanOffset {
    userPan = clampProductionPan(next.x, next.z, currentPanBounds());
    const { width, height } = measureViewport();
    frameCamera(width, height);
    renderer.render(scene, camera);
    return { ...userPan };
  }

  function applyUserZoom(next: number): number {
    userZoom = clampProductionZoom(next);
    const { width, height } = measureViewport();
    frameCamera(width, height);
    renderer.render(scene, camera);
    return userZoom;
  }

  const hemi = new THREE.HemisphereLight(0xd6ecff, 0xb98e5c, 0.7);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xfff0d2, 1.95);
  sun.position.set(-9.5, 16.5, 8.5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.bias = -0.0005;
  sun.shadow.normalBias = 0.035;
  sun.shadow.radius = 3;
  const shadowExtent = 15;
  sun.shadow.camera.left = -shadowExtent;
  sun.shadow.camera.right = shadowExtent;
  sun.shadow.camera.top = shadowExtent;
  sun.shadow.camera.bottom = -shadowExtent;
  sun.shadow.camera.near = 2;
  sun.shadow.camera.far = 50;
  scene.add(sun);
  const rim = new THREE.DirectionalLight(0xbfd8ff, 0.32);
  rim.position.set(12, 6, -14);
  scene.add(rim);
  const ambient = new THREE.AmbientLight(0xffe6c8, 0.22);
  scene.add(ambient);

  function applyTimeOfDay(band: TimeOfDayBand): void {
    const look = timeOfDayVisual(band);
    hemi.color.setHex(look.hemiSky);
    hemi.groundColor.setHex(look.hemiGround);
    hemi.intensity = look.hemiIntensity;
    sun.color.setHex(look.sunColor);
    sun.intensity = look.sunIntensity;
    sun.position.set(look.sunPosition.x, look.sunPosition.y, look.sunPosition.z);
    rim.color.setHex(look.rimColor);
    rim.intensity = look.rimIntensity;
    ambient.color.setHex(look.ambientColor);
    ambient.intensity = look.ambientIntensity;
    scene.environmentIntensity = look.environmentIntensity;
    renderer.toneMappingExposure = look.exposure;
    // COL-011B: lamps, bulb strings, halos, ground pools and the festive floor follow the band.
    applyNightLook(mats, [carouselChannels.lights, coasterChannels.lights, ...SHARED_LIGHT_CHANNELS], look);
    if (typeof document !== 'undefined') {
      document.body.style.background = `linear-gradient(180deg, ${hexCss(look.skyTop)} 0%, ${hexCss(look.skyBottom)} 100%)`;
      document.body.dataset.tod = look.band;
    }
  }

  const mats = createMaterials();
  const carouselChannels = createChannelMaterials(CAROUSEL_PALETTES.candyStripe.palette);
  const coasterChannels = createChannelMaterials(COASTER_PALETTES.classicRed.palette);
  applyApprovedPalettesToChannels(model, carouselChannels, coasterChannels);
  applyTimeOfDay(resolveTimeOfDayBand(new Date(), typeof window !== 'undefined' ? window.location.search : ''));
  if (typeof window !== 'undefined') {
    window.setInterval(() => {
      applyTimeOfDay(resolveTimeOfDayBand(new Date(), window.location.search));
    }, 60_000);
  }
  let park = createParkFromState(mats, carouselChannels, coasterChannels, model);
  let elapsed = 0;
  scene.add(park.root);

  function measureViewport(): { width: number; height: number } {
    // UI-019-C1 checked this max() path. On mobile it can prefer innerHeight over the
    // smaller visualViewport, but the physical empty-lower-region finding is explained
    // by portrait shiftY even when the camera aspect matches the CSS box. Leave it.
    const visual = window.visualViewport;
    return {
      width: Math.max(mount.clientWidth, visual?.width ?? window.innerWidth, 320),
      height: Math.max(mount.clientHeight, visual?.height ?? window.innerHeight, 480),
    };
  }

  function resize(): void {
    const { width, height } = measureViewport();
    renderer.setSize(width, height, false);
    frameCamera(width, height);
  }

  function rotateView(delta: -1 | 1): number {
    cameraQuarter = normalizedCameraQuarter(cameraQuarter + delta);
    const { width, height } = measureViewport();
    frameCamera(width, height);
    renderer.render(scene, camera);
    return cameraQuarter;
  }

  function applyApprovedPalettes(next: MigrationRenderModel): {
    carouselPaletteId: string;
    coasterPaletteId: string;
  } {
    currentModel = next;
    host.model = next;
    return applyApprovedPalettesToChannels(next, carouselChannels, coasterChannels);
  }

  function rebuild(next: MigrationRenderModel): void {
    scene.remove(park.root);
    applyApprovedPalettes(next);
    park = createParkFromState(mats, carouselChannels, coasterChannels, next);
    scene.add(park.root);
    host.parkTop = park.parkTop;
    host.lifecycle.parkRebuilds += 1;
    const { width, height } = measureViewport();
    frameCamera(width, height);
  }

  function syncFloors(next: MigrationRenderModel): void {
    applyApprovedPalettes(next);
    syncParkFloorLayers(park.root, next);
    host.lifecycle.floorSyncs += 1;
  }

  function syncSelection(next: MigrationRenderModel): void {
    applyApprovedPalettes(next);
    syncParkSelectionLayer(park.root, next);
    host.lifecycle.selectionSyncs += 1;
  }

  function syncLandOverlays(next: MigrationRenderModel): void {
    applyApprovedPalettes(next);
    syncParkLandOverlayLayer(park.root, next);
    host.lifecycle.landSyncs += 1;
  }

  function syncEntities(next: MigrationRenderModel): void {
    applyApprovedPalettes(next);
    syncParkEntityLayer(park.root, next);
    host.lifecycle.entitySyncs += 1;
  }

  function renderOnce(): void {
    renderer.render(scene, camera);
  }

  const host: MigrationSceneHost = {
    renderer,
    scene,
    camera,
    canvas: renderer.domElement,
    parkTop: park.parkTop,
    model,
    lifecycle: {
      hostCreates: 1,
      parkRebuilds: 0,
      floorSyncs: 0,
      selectionSyncs: 0,
      landSyncs: 0,
      entitySyncs: 0,
    },
    rebuild,
    syncFloors,
    syncSelection,
    syncLandOverlays,
    syncEntities,
    applyApprovedPalettes,
    renderOnce,
    rotateView,
    viewQuarter: () => cameraQuarter,
    zoomBy: (delta) => applyUserZoom(userZoom + delta),
    setZoomFactor: (value) => applyUserZoom(value),
    zoomFactor: () => userZoom,
    panBy: (delta) => applyUserPan({ x: userPan.x + delta.x, z: userPan.z + delta.z }),
    setPanOffset: (value) => applyUserPan(value),
    panOffset: () => ({ ...userPan }),
    elapsedSeconds: () => elapsed,
    queueEvidence: () => park.root.userData.queueSnapshot(elapsed) as QueueEvidence,
    advanceElapsed(seconds: number) {
      elapsed += Math.max(0, seconds);
      park.root.userData.update(elapsed, 0.016);
      renderOnce();
      return host.queueEvidence();
    },
    startLoop(onFrame) {
      let clock = createForegroundLoopClock(performance.now(), documentIsHidden());
      const onVisibility = (): void => {
        clock = noteForegroundVisibility(clock, performance.now(), documentIsHidden());
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility);
      }
      const step = (): void => {
        const stepped = stepForegroundLoopClock(clock, performance.now(), documentIsHidden());
        clock = stepped.clock;
        const visualDeltaSeconds = stepped.visualDeltaMs / 1000;
        elapsed += visualDeltaSeconds;
        onFrame?.(stepped.visualDeltaMs, stepped.incomeDeltaMs);
        park.root.userData.update(elapsed, visualDeltaSeconds);
        renderer.render(scene, camera);
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    resize,
  };

  window.addEventListener('resize', resize);
  window.visualViewport?.addEventListener('resize', resize);
  resize();
  return host;
}
